import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import EditIcon from '@material-ui/icons/Edit';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import MuiListItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItem';
import ListItemSecondaryAction from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItemSecondaryAction';
import { useListContextDispatch } from 'Components/UI/ListDetailView/ListContext';
import { useSelectionContextState } from 'Components/UI/ListDetailView/SelectionContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import ListItemText from './ListItemText';
import useMissionControl from './useMissionControl';
import EditMeetingDialog from './EditMeetingDialog';
import ListItemIcon from './ListItemIcon';

const styles = theme => ({
  root: {
    '&.Mui-selected, &.Mui-selected:hover': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.facet.accentBlue11,
    },
  },
  editIconButton: {},
  listItemSecondaryAction: {},
  listItemSecondaryActionHidden: {
    display: 'none',
  },
});

const ListItem = ({
  className,
  classes,
  onClick,
  resource,
  selected,
  ...rest
}) => {
  const { logEvent, rootNodeProps } = useEventTrackingLogEvent();
  const listDispatch = useListContextDispatch();
  const selectionState = useSelectionContextState();
  const { queryValName } = selectionState;
  const [isEditing, setIsEditing] = React.useState(null);
  const { selectMeetingResource } = useMissionControl();
  const eventTypePrompt = resource
    .meta()
    .field('meetingType')
    .selectedOption()
    .attr('prompt');
  const eventStartTime = moment(resource.attr('startTime')).format('l');
  const resourceSelfLink = resource.link('self').url();
  const search = new URLSearchParams(window.location.search);

  search.set(queryValName, resourceSelfLink);

  return (
    <>
      <MuiListItem
        {...rootNodeProps}
        button
        className={classNames(classes.root, className)}
        component={Link}
        key={resource.link('self').url()}
        onClick={async () => {
          logEvent('Clicked Meeting');
          await selectMeetingResource(resource);
          onClick();
        }}
        to={{
          pathname: window.location.pathname,
          search: search.toString(),
        }}
        selected={selected}
        test={`mission-control-list-item eventType:${eventTypePrompt} eventStartTime:${eventStartTime}`}
        {...rest}
      >
        <ListItemIcon
          resource={resource}
          selected={selected}
        />
        <ListItemText
          resource={resource}
          selected={selected}
        />
        <ListItemSecondaryAction
          className={classNames(classes.listItemSecondaryAction, {
            [classes.listItemSecondaryActionHidden]: !selected,
          })}
        >
          <IconButton
            className={classes.editIconButton}
            color={'white'}
            icon={EditIcon}
            size={'small'}
            disabled={resource.attr('calendarEventId') === ''}
            test={`mission-control-list-item-edit-button eventType:${eventTypePrompt} eventStartTime:${eventStartTime}`}
            onClick={(evt) => {
              logEvent('Opened Edit Meeting Dialog');
              evt.stopPropagation();
              evt.preventDefault();
              setIsEditing(true);
            }}
          />
        </ListItemSecondaryAction>
      </MuiListItem>
      {isEditing ? (
        <EditMeetingDialog
          onClose={(viaSubmit) => {
            if (!viaSubmit) {
              logEvent('Closed Edit Meeting Dialog');
            }
            setIsEditing(false);
          }}
          onSubmit={(resp) => {
            logEvent('Submitted Edit Meeting Dialog');
            const [updatedResource] = resp;
            listDispatch({ type: 'updateResource', resource: updatedResource });
          }}
          resource={resource}
        />
      ) : null}
    </>
  );
};

ListItem.defaultProps = {
  className: '',
};

ListItem.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  resource: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
};

export default withStyles(styles)(ListItem);
